import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridModule, ICellRendererAngularComp } from 'ag-grid-angular';
import { GridApi, GridOptions, ICellRendererParams, RowSelectionOptions } from 'ag-grid-community'

import { KnowledgeBaseItemService } from '../../../services/knowledge-base-item.service';
import { ServiceAreaService } from '../../../services/service-area.service';
import { TenantService } from '../../../services/tenant.service';
import { ListStateService } from '../../../services/list-state.service';
import { ReadOnlyIfNoGroupPermissionDirectiveDirective } from '../../../directives/read-only-if-no-group-permission-directive.directive';

// Constants
const KNOWLEDGE_BASE_ITEM_EDIT_PATH = '/knowledge-base-item/edit';

@Component({
  selector: 'app-knowledge-base-item-list',
  imports: [CommonModule, FormsModule, AgGridModule, RouterModule, ReadOnlyIfNoGroupPermissionDirectiveDirective],
  templateUrl: './knowledge-base-item-list.component.html',
  styleUrl: './knowledge-base-item-list.component.scss'
})
export class KnowledgeBaseItemListComponent implements OnInit {

  @ViewChild('knowledgeBaseGrid') knowledgeBaseGrid: any;

  // **Template references for modals**
  @ViewChild('deleteConfirmModal') deleteModal!: any;
  @ViewChild('moveModal') moveModal!: any;

  // **Route & Grid Variables**
  serviceAreaId: any;
  serviceAreaName: any;
  gridOptions: GridOptions;
  gridApi!: GridApi;

  // **Data Lists**
  knowledgeBaseItems: any[] = [];
  filteredKnowledgeBaseItems: any[] = [];
  serviceAreas: any[] = [];

  // **UI Controls**
  searchQuery: string = '';
  isCheckboxVisible: boolean = false;
  anySelected: boolean = false;
  moveDetails: any = {};

  rowSelection: any = { mode: 'singleRow', checkboxes: false, headerCheckbox: false };

  isAllowedTenant: any;
  firstVisibleRowIndex: number = 0;

  constructor(
    private kbService: KnowledgeBaseItemService,
    private route: ActivatedRoute,
    private router: Router,
    private serviceAreaService: ServiceAreaService,
    private modalService: NgbModal,
    private tenantService: TenantService,
    private listStateService: ListStateService
  ) {
    this.isAllowedTenant = this.tenantService.isAllowedTenant();
    this.gridOptions = this.initializeGridOptions();
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.serviceAreaId = params['serviceAreaId'];

      const savedState = this.listStateService.getListState(this.serviceAreaId);
      if (savedState) {
        this.searchQuery = savedState.searchQuery;
        this.firstVisibleRowIndex = savedState.firstVisibleRowIndex;
      }


      this.loadKnowledgeBaseItems();
      this.loadServiceAreas();
    })

  }


  //#region Grid Settings and events

  private initializeGridOptions(): GridOptions {
    return {
      columnDefs: [
        { field: 'description', sortable: true, filter: true, resizable: true, flex: 1 }, // Added flex: 1 for better distribution        
        { field: 'webIntents', sortable: true, filter: true, resizable: true },
        { field: 'phoneIntents', sortable: true, filter: true, resizable: true },
        { field: 'autoEscalate', sortable: true, filter: true, resizable: true, width: 80, hide: !this.isAllowedTenant  },
        { field: 'webAnswer', headerName: 'Web', cellRenderer: this.booleanCellRenderer.bind(this), resizable: true, width: 80 }, // Fixed width
        { field: 'smsAnswer', headerName: 'SMS', cellRenderer: this.booleanCellRenderer.bind(this), resizable: true, width: 80 }, // Fixed width
        { field: 'voiceAnswer', headerName: 'Voice', cellRenderer: this.booleanCellRenderer.bind(this), resizable: true, width: 80 }, // Fixed width
        { field: 'escalationDepartmentId', headerName: 'Escalation', cellRenderer: this.escalationCellRenderer.bind(this), resizable: true, width: 100 }, // Fixed width
        {
          field: 'id',
          headerName: 'Actions',
          headerClass: 'header-center',
          cellRenderer: ActionsCellRenderer,
          cellRendererParams: {
            context: {
              onDeleteAction: this.openDeleteModal.bind(this),
              onEditAction: this.onUpdateItem.bind(this),
              onMoveAction: this.openMoveModal.bind(this)
            }
          },
          resizable: true,
          width: 150,
          pinned: 'right' // Pinned to the right
        }
      ],
      suppressScrollOnNewData: true,
    };
  }

  onGridReady(params: any) {
    this.gridApi = params.api;    
    setTimeout(() => {
      if (this.firstVisibleRowIndex > 0) {
        this.gridApi.ensureIndexVisible(this.firstVisibleRowIndex, 'middle');
      }
    }, 100);
  }

  booleanCellRenderer(params: any) {

    var title: string = "";

    switch (params.colDef.headerName) {
      case 'Web':
        title = "Web Answer";
        break;
      case 'SMS':
        title = "SMS Answer";
        break;
      case 'Voice':
        title = "Voice Answer";
        break;
      default:
        break;
    }

    const value = params.value;
    const icon = value ? 'bi-check-circle text-success' : 'bi-x-circle text-danger';
    return `<i class="bi ${icon}" style="font-size: 1.5rem;" title="${title}" data-bs-toggle="tooltip"></i>`; // Increased icon size
  }

  escalationCellRenderer(params: any) {
    const value = params.value;
    const icon = value ? 'bi-check-circle text-info' : 'bi-x-circle text-danger';
    return `<i class="bi ${icon}" style="font-size: 1.5rem;" title="Escalation Department Selected" data-bs-toggle="tooltip"></i>`; // Increased icon size
  }

  toggleCheckboxVisibility() {
    this.isCheckboxVisible = !this.isCheckboxVisible;

    const mode = this.isCheckboxVisible ? 'multiRow' : 'singleRow';
    const checkboxes = this.isCheckboxVisible;
    const headerCheckbox = this.isCheckboxVisible;

    this.rowSelection = {
      mode: mode,
      checkboxes: checkboxes,
      headerCheckbox: headerCheckbox
    };

    this.gridApi.setGridOption('rowSelection', this.rowSelection);
  }

  onSelectionChanged() {
    var selectedRows = this.gridApi.getSelectedRows();
    this.anySelected = selectedRows.length > 0;
    this.moveDetails.selectedCount = selectedRows.length;
  }
  //#endregion


  // **Fetch and filter Data Methods**
  private loadKnowledgeBaseItems(): void {
    this.gridApi?.setGridOption('loading', true);

    this.kbService.getKnowledgeBaseItems(this.serviceAreaId).subscribe({
      next:
        (data: any) => {
          this.knowledgeBaseItems = data.sort((a: any, b: any) => a.description.localeCompare(b.description));
          console.log(this.knowledgeBaseItems);
          this.filteredKnowledgeBaseItems = [...this.knowledgeBaseItems];
          this.filterKnowledgeBaseItems(); // Filter the items based on the search query
          this.gridApi?.setGridOption("rowData", this.filteredKnowledgeBaseItems);
        },
      complete: () => { this.gridApi?.setGridOption('loading', false); },
      error: (error: any) => { this.gridApi?.setGridOption('loading', false); }
    });

    this.serviceAreaService.getServiceNameById(this.serviceAreaId).subscribe({
      next: (data: any) => {
        this.serviceAreaName = data.name;
      },
      error: (error: any) => {
        console.error('Error fetching service area name:', error);
      }
    });
  };

  loadServiceAreas() {
    this.serviceAreaService.getServiceAreas().subscribe(data => {
      this.serviceAreas = data.sort((a: any, b: any) => a.name.localeCompare(b.name));
    });
  }

  filterKnowledgeBaseItems(): void {
    const query = this.searchQuery.toLowerCase();

    this.filteredKnowledgeBaseItems = this.knowledgeBaseItems.filter(item =>
      Object.values(item).some(value =>
        value?.toString().toLowerCase().includes(query)
      ) ||
      Object.values(item.intent || {}).some(value =>
        value?.toString().toLowerCase().includes(query)
      )
    );

    this.gridApi?.setGridOption("rowData", this.filteredKnowledgeBaseItems);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filteredKnowledgeBaseItems = [...this.knowledgeBaseItems];
  }



  // ** Modals

  openBulkMoveModal(content: any) {
    this.moveDetails.targetServiceAreaId = null;
    this.modalService.open(content, { size: 'lg' });
  }

  openMoveModal(item: any): void {
    this.moveDetails = {};
    this.moveDetails.item = item;
    this.modalService.open(this.moveModal, { size: 'lg' });
  }

  openDeleteModal(item: any) {
    this.deleteModal.idToDelete = item.id;
    this.modalService.open(this.deleteModal, { centered: true });
  }


  // ** Data Operations **

  bulkMoveItem(modal: any) {
    var selectedItems = this.gridApi.getSelectedRows();
    const itemIds = selectedItems.map(item => item.id);

    this.kbService.bulkMoveKnowledgeBaseItems(itemIds, this.moveDetails.targetServiceAreaId).subscribe({
      next: () => {
        alert('Selected items moved successfully.');
        modal.close();
        this.loadKnowledgeBaseItems(); // Refresh the list
      },
      error: (error: any) => {
        alert('An error occurred while moving the selected items.');
        console.error(error);
      }
    });
  }

  moveItem(modal: any) {
    this.kbService.moveKnowledgeBaseItem(this.moveDetails.item.id, this.moveDetails.targetServiceAreaId).subscribe({
      next: () => {
        alert('Knowledge Base Item moved successfully.');
        modal.close();
        this.loadKnowledgeBaseItems(); // Reload the updated list
      },
      error: (error: any) => {
        alert('An error occurred while moving the Knowledge Base Item.');
        console.error(error);
      }
    });


    throw new Error('Method not implemented.');
  }

  confirmDeleteItem(modal: any) {
    this.kbService.deleteKnowledgeBaseItem(this.deleteModal.idToDelete).subscribe({
      next: () => {
        this.loadKnowledgeBaseItems()
      },
      error: (error: any) => {
        console.error('Error deleting item:', error);
      },
      complete: () => { modal.close(); }
    });
  }

  onUpdateItem(item: any): void {
    this.firstVisibleRowIndex = this.gridApi.getFirstDisplayedRowIndex();    
    this.listStateService.setListState(this.serviceAreaId, this.searchQuery, this.firstVisibleRowIndex);

    this.router.navigate([KNOWLEDGE_BASE_ITEM_EDIT_PATH, item.id]);
  }


  // ** Import items **
  selectedFile: File | null = null;

  onFileSelected(event: any): void {
    if (event.target.files.length > 0) {
      this.selectedFile = event.target.files[0];

      this.importKnowledgeBaseItems();
    }
  }

  importKnowledgeBaseItems(): void {
    if (!this.selectedFile) {
      alert('Please select a file first.');
      return;
    }

    this.kbService.importKnowledgeBaseItemsToServiceArea(this.selectedFile, this.serviceAreaId).subscribe({
      next: () => {
        alert('Knowledge Base Items imported successfully.');
        this.loadKnowledgeBaseItems(); // Reload data
        this.selectedFile = null; // Reset file input
      },
      error: (error) => {
        alert('Error importing file. Please check the format.');
        console.error(error);
      }
    });
  }

}





/** ✅ Actions Cell Renderer Component */
@Component({
  standalone: true,
  imports:[ReadOnlyIfNoGroupPermissionDirectiveDirective],
  templateUrl: './ActionsCellRenderer.html'
})
export class ActionsCellRenderer implements ICellRendererAngularComp {

  params: ICellRendererParams<any, any, any> | undefined;

  agInit(params: ICellRendererParams): void {
    this.params = params;
  }

  onDelete() { this.params?.context.onDeleteAction(this.params.data); }
  onEdit() { this.params?.context.onEditAction(this.params.data); }
  onMove() { this.params?.context.onMoveAction(this.params.data); }
  refresh(params: ICellRendererParams) { return true; }
}

